import { useContext, useState } from 'react'

import { useCallMutation } from '@/common/api/client'
import { apiPostFetcher, apiPutFetcher } from '@/common/api/fetcher'
import { useFtpConnectionsGetApi } from '@/common/api/modules/ftpSettings'
import { useSystemConfigGetApi } from '@/common/api/modules/systemConfig'
import {
  ResetFtpPasswordResponse,
  ResetFtpPasswordVars,
  UpdateFtpSettingsResponse,
  UpdateFtpSettingsVars
} from '@/common/api/params/ftpSettings'
import { ApiPath } from '@/common/api/path'
import { extractSystemConfigValue } from '@/common/app/modules/systemConfig'
import { Site } from '@/common/model/site'
import { SystemConfigKey } from '@/common/model/systemConfig'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import CheckBox, { CheckBoxType } from '@/components/view/atoms/checkbox/CheckBox'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import { MessageType } from '@/components/view/molecules/bar/MessageBar'

import { ConfirmDialogContext } from '@/components/container/modal/dialog/confirm/ConfirmDialogProvider'
import { ToastMessageContext } from '@/components/container/utils/message/ToastMessageProvider'

interface Props {
  site: Site
  onRefetch?: () => void
  isDirty: boolean
}

const SiteFormFtpInput = ({ site, onRefetch, isDirty }: Props) => {
  const { data: systemConfig } = useSystemConfigGetApi()
  const { data: ftpConnectionList } = useFtpConnectionsGetApi()
  const [isPasswordShowing, setIsPasswordShowing] = useState(false)
  const { showModal, hide } = useContext(ConfirmDialogContext)

  const ftpHost = systemConfig
    ? extractSystemConfigValue(systemConfig, SystemConfigKey.FTP_HOST)
    : undefined
  const ftpConnections = ftpConnectionList?.connections ?? []
  // 案内文の「〜で送信」に使う。例: FTPまたはSFTP
  const ftpProtocolText = ftpConnections.map((c) => c.protocol).join('または') || 'FTP'

  const { trigger: triggerToggle, isMutating: isToggling } = useCallMutation<
    UpdateFtpSettingsResponse,
    UpdateFtpSettingsVars
  >(ApiPath.UPDATE_FTP_SETTINGS, { onSuccess: onRefetch }, apiPutFetcher)

  const { trigger: triggerReset } = useCallMutation<ResetFtpPasswordResponse, ResetFtpPasswordVars>(
    ApiPath.RESET_FTP_PASSWORD,
    { onSuccess: onRefetch },
    apiPostFetcher
  )
  const { addMessage } = useContext(ToastMessageContext)

  const handleToggle = () => {
    triggerToggle({ site_id: site.id, ftp_enabled: !site.ftp_enabled })
  }

  const handleResetPassword = () => {
    showModal({
      message: 'FTPパスワードを再生成しますか？EC側への再連絡が必要になります。',
      noText: 'キャンセル',
      yesText: '再生成',
      buttonColor: 'critical',
      onClickYes: async () => {
        await triggerReset({ site_id: site.id })
        hide()
        addMessage({ text: 'FTPパスワードを再生成しました', type: MessageType.SUCCESS })
      }
    })
  }

  const handleCopyInfo = () => {
    const text = [
      '【FTP接続情報】',
      `ホスト: ${ftpHost}`,
      ...ftpConnections.map((c) => `${c.protocol}ポート: ${c.port}`),
      `ユーザー名: ${site.ftp_username}`,
      `パスワード: ${site.ftp_password}`,
      '',
      `各ファイルを${ftpProtocolText}で送信いただき、`,
      'ファイルの受信、及びw2SmartRecommendに情報が登録されるか',
      'ご確認ください。',
      '',
      '実行状況は、ファイル取込みメニューから、',
      '「成功や失敗」のログを確認することができます。',
      '',
      '失敗時は3回までリトライを行い、終了します。',
      'エラーなど取り込めない場合は、ファイルを修正の上、',
      `再度${ftpProtocolText}で新しいファイルを送信、または、`,
      '管理画面より手動取込みを行っていただく必要がございます。'
    ].join('\n')
    navigator.clipboard.writeText(text).then(() => {
      addMessage({ text: '接続情報をコピーしました', type: MessageType.SUCCESS })
    })
  }

  return (
    <Area>
      <AreaContent>
        <FormRow align='center'>
          <Text weight='bold'>FTP接続情報</Text>
          <CheckBox
            name='ftp_enabled'
            type={CheckBoxType.TOGGLE_TEXT}
            toggleText={{
              left: '有効',
              right: '無効'
            }}
            checked={site.ftp_enabled}
            onChange={handleToggle}
            disabled={isToggling || isDirty}
          />
        </FormRow>
        {site.ftp_enabled && site.ftp_username ? (
          <>
            <FormRow align='center' gap={12}>
              <FormLabel fixWidth={80}>ホスト</FormLabel>
              <Text>{ftpHost || '-'}</Text>
            </FormRow>
            {ftpConnections.map((c) => (
              <FormRow key={c.protocol} align='center' gap={12}>
                <FormLabel fixWidth={80}>{c.protocol}ポート</FormLabel>
                <Text>{c.port}</Text>
              </FormRow>
            ))}
            <FormRow align='center' gap={12}>
              <FormLabel fixWidth={80}>ユーザー名</FormLabel>
              <Text>{site.ftp_username}</Text>
            </FormRow>
            <FormRow align='center' gap={12}>
              <FormLabel fixWidth={80}>パスワード</FormLabel>
              <Text>{isPasswordShowing ? site.ftp_password : '••••••••••••'}</Text>
            </FormRow>
            <ItemAlign>
              <Button
                onClick={() => setIsPasswordShowing((isPasswordShowing) => !isPasswordShowing)}
                color={isPasswordShowing ? ButtonColor.SECONDARY : ButtonColor.PRIMARY}
              >
                {isPasswordShowing ? 'パスワードを隠す' : 'パスワードを表示'}
              </Button>
              <Button color={ButtonColor.CRITICAL} onClick={handleResetPassword} disabled={isDirty}>
                パスワード再生成
              </Button>
            </ItemAlign>
            <div className='mt-2'>
              <Button onClick={handleCopyInfo} color={ButtonColor.SECONDARY}>
                接続情報をコピー
              </Button>
            </div>
          </>
        ) : !site.ftp_enabled ? (
          <Text color={TextColor.SECONDARY}>
            FTP連携が無効です。有効にするとEC側にFTP接続情報を案内できます。
          </Text>
        ) : null}
      </AreaContent>
    </Area>
  )
}

export default SiteFormFtpInput
